import { ReactNode, FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { InformationCircleIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Breadcrumb } from 'flowbite-react';

import { SkeletonTextLoading } from '@restworld/ui-components';
import {
  IBadgeProps,
  Badge,
  Button as ButtonV2,
  Heading,
  IconButton,
  Link,
  Tooltip,
  type IconComponentType,
} from '@restworld/ui-ds-base';
import { cn, isDev, useResponsiveBreakPoints } from '@restworld/utils-common';

import BackButton from '../components/back-button';
import { EmployerPrimaryLayoutProps } from './employer-primary-layout';

const BreadcrumbItem = Breadcrumb.Item;

type EmployerPrimaryHeaderProps = Pick<
  EmployerPrimaryLayoutProps,
  | 'pageTitle'
  | 'addButtonTitle'
  | 'onAddButtonClick'
  | 'addButtonLoading'
  | 'addButtonDisabled'
  | 'addButtonTooltip'
  | 'onInfoIconClick'
> & {
  goToPreviousPage?: () => void;
  customHeaderStyles?: string;
  showInfoIcon?: boolean;
  customTitleElement?: ReactNode;
  customRightElement?: ReactNode;
  renderCustomElement?: ReactNode;
  isKanbanPageLayout?: boolean;
  isLoading?: boolean;
  breadCrumbs?: {
    label: string;
    href?: string;
    icon?: IconComponentType;
    truncateLabel?: boolean;
  }[];
  isLastBreadCrumbInteractive?: boolean;
  badge?: { variant: IBadgeProps['variant']; text: string };
};

const ButtonWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('text-neutral-700', className)} {...props} />
);

const ButtonAndTitleWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex gap-2 flex-row justify-start items-center w-fit', className)} {...props} />
);

const HeaderWrapper = ({
  customHeaderStyles,
  className,
  ...props
}: { customHeaderStyles?: string } & React.HTMLAttributes<HTMLElement>) => (
  <header
    className={cn('flex flex-col justify-between gap-3 lg:flex-row lg:items-center', customHeaderStyles, className)}
    {...props}
  />
);
const BreadcrumbStyled = ({ className, ...props }: React.ComponentProps<typeof Breadcrumb>) => (
  <Breadcrumb
    className={cn(
      '[&_.breadcrumb-item>svg]:text-neutral-700 [&_.breadcrumb-item>svg]:ml-0.5 [&_.breadcrumb-item>svg]:mr-0.5 [&_.breadcrumb-item>svg]:max-w-[1rem] [&_.breadcrumb-item>svg]:max-h-4 [&_.breadcrumb-item>svg]:min-w-[1rem] [&_.breadcrumb-item>svg]:min-h-[1rem]',
      className
    )}
    {...props}
  />
);
export const EmployerPrimaryLayoutHeader: FC<EmployerPrimaryHeaderProps> = ({
  pageTitle,
  addButtonTitle,
  onAddButtonClick,
  addButtonLoading,
  addButtonDisabled,
  addButtonTooltip,
  goToPreviousPage,
  onInfoIconClick,
  customHeaderStyles,
  showInfoIcon = true,
  customTitleElement,
  customRightElement,
  renderCustomElement,
  isKanbanPageLayout,
  isLoading,
  breadCrumbs,
  isLastBreadCrumbInteractive,
  badge,
}) => {
  const { isSmallMobile } = useResponsiveBreakPoints();
  const { t: tC } = useTranslation('common');
  const renderBreadcrumbs = useMemo(
    () =>
      breadCrumbs?.length ? (
        <BreadcrumbStyled className="breadcrumb" aria-label="breadcrumb">
          {breadCrumbs.map(({ label, href, truncateLabel, icon }, i) => {
            const breadcrumbTitle = (
              <Heading size={6} weight="medium" color="neutral" maxChars={isSmallMobile && truncateLabel ? 12 : null}>
                {label}
              </Heading>
            );
            return (
              <BreadcrumbItem
                key={label + i}
                {...{
                  as: 'li',
                }}
                className={`breadcrumb-item breadcrumb-item-${i}`}
              >
                {breadCrumbs.length - 1 === i && !isLastBreadCrumbInteractive ? (
                  breadcrumbTitle
                ) : (
                  <Link href={isDev ? href : `/v2${href}`} StartIcon={icon} newTab={false} {...{ as: 'div' }}>
                    {breadcrumbTitle}
                  </Link>
                )}
              </BreadcrumbItem>
            );
          })}
        </BreadcrumbStyled>
      ) : null,
    [breadCrumbs, isLastBreadCrumbInteractive, isSmallMobile]
  );

  const renderBackButton = useMemo(
    () =>
      goToPreviousPage ? (
        <ButtonWrapper>
          <BackButton className={'relative'} onClick={goToPreviousPage} />
        </ButtonWrapper>
      ) : null,
    [goToPreviousPage]
  );

  const renderTitleAndInfoButton = useMemo(
    () =>
      isLoading ? (
        <div className="flex flex-col gap-2">
          <SkeletonTextLoading width="rect-box" />
          <SkeletonTextLoading width="long" />
        </div>
      ) : (
        (customTitleElement || pageTitle) && (
          <div className="flex items-center gap-3">
            {customTitleElement || (
              <>
                <Heading size={2}>{pageTitle ? tC(pageTitle) : ''}</Heading>
                {showInfoIcon && (
                  <IconButton Icon={InformationCircleIcon} ariaLabel={tC('view_info')} onClick={onInfoIconClick} />
                )}
                {badge && (
                  <Badge variant={badge.variant} className="w-fit">
                    {badge.text}
                  </Badge>
                )}
              </>
            )}
          </div>
        )
      ),
    [isLoading, customTitleElement, pageTitle, tC, showInfoIcon, onInfoIconClick, badge]
  );

  const renderAddButton = useMemo(() => {
    if (!addButtonTitle) return null;
    // When the button is disabled with an explanatory tooltip, keep it
    // interactive (no HTML `disabled` attr, no `pointer-events: none`) so
    // hover still triggers the Tooltip. Click is neutralised and the button
    // is styled as disabled. `aria-disabled` preserves a11y semantics.
    const isDisabledWithTooltip = !!addButtonDisabled && !!addButtonTooltip;
    const button = (
      <ButtonV2
        StartIcon={PlusIcon}
        className={cn('mt-4 w-full max-w-full lg:w-fit', isDisabledWithTooltip && 'opacity-50 !cursor-not-allowed')}
        onClick={isDisabledWithTooltip ? undefined : onAddButtonClick}
        loading={addButtonLoading}
        disabled={addButtonDisabled && !addButtonTooltip}
        aria-disabled={isDisabledWithTooltip || undefined}
        id="layout-cta-button"
      >
        {tC(addButtonTitle)}
      </ButtonV2>
    );
    if (addButtonTooltip) {
      return (
        <Tooltip content={addButtonTooltip} placement="bottom">
          {button}
        </Tooltip>
      );
    }
    return button;
  }, [addButtonTitle, onAddButtonClick, addButtonLoading, addButtonDisabled, addButtonTooltip, tC]);
  const isBreadcrumbPresent = breadCrumbs?.length > 0;
  if (isKanbanPageLayout)
    return (
      <>
        <div className="block lg:hidden">
          <HeaderWrapper className="flex flex-col gap-3" customHeaderStyles={customHeaderStyles}>
            <div className="w-full flex flex-row justify-between gap-2 items-center">
              {renderBackButton}
              {customRightElement}
            </div>
            <ButtonAndTitleWrapper className="flex-col items-start justify-center">
              {renderBreadcrumbs}
              {renderTitleAndInfoButton}
            </ButtonAndTitleWrapper>
          </HeaderWrapper>
          {renderCustomElement}
        </div>
        <div className="hidden lg:block">
          <div className="flex flex-row gap-4 items-center mb-3">
            {renderBackButton}
            {renderBreadcrumbs}
          </div>
          <HeaderWrapper
            className="pt-0 pb-[16px] justify-start flex flex-row items-center"
            customHeaderStyles={customHeaderStyles}
          >
            <ButtonAndTitleWrapper className="flex-row justify-start items-center">
              {renderTitleAndInfoButton}
              {customRightElement}
            </ButtonAndTitleWrapper>
          </HeaderWrapper>
          {renderCustomElement}
        </div>
      </>
    );
  return (
    <>
      <HeaderWrapper customHeaderStyles={customHeaderStyles}>
        {!isBreadcrumbPresent ? (
          <div className="flex flex-col gap-4">
            <ButtonAndTitleWrapper>
              {renderBackButton}
              {renderTitleAndInfoButton}
            </ButtonAndTitleWrapper>
            {renderCustomElement}
          </div>
        ) : (
          <>
            <div className="block lg:hidden">
              {renderBackButton}
              {renderBreadcrumbs}
              <ButtonAndTitleWrapper>{renderTitleAndInfoButton}</ButtonAndTitleWrapper>
            </div>
            <div className="hidden lg:block">
              <ButtonAndTitleWrapper>
                {renderBackButton}
                {renderTitleAndInfoButton}
              </ButtonAndTitleWrapper>
              {renderBreadcrumbs}
            </div>
          </>
        )}
        {customRightElement}
        {renderAddButton}
      </HeaderWrapper>
    </>
  );
};

export default EmployerPrimaryLayoutHeader;
